/*!
 * Universal Attribution Tracker v2
 * ---------------------------------------------------------------
 * Works across subdomains (shared root cookie) AND across different
 * root domains (link decoration). Platform-aware: auto-detects
 * Shopify / WordPress / Salesforce Commerce Cloud / generic.
 *
 * - Captures UTMs + all major ad click IDs
 * - Stores FIRST-TOUCH (180d) and LAST-TOUCH (30d) cookies on the
 *   registrable root domain so every subdomain sees the same values
 * - Propagates to internal links, own-domain links (cross-TLD), and
 *   external links (unless blocklisted)
 * - Watches DOM for dynamically added links
 * - Auto-fills hidden form fields in Shopify / WP / SFCC / generic forms
 * - Pushes cart attributes on Shopify automatically
 * - Exposes window.Attribution
 *
 * Deploy the SAME file on every property you own.
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  // ============ CONFIG ============
  var CONFIG = {
    // --- Cross-domain setup ---
    // 'auto' derives the registrable root (e.g. adrenaline.com from shop.adrenaline.com)
    // Override with a string like 'adrenaline.com' if auto-detection gets it wrong.
    // Set to '' (empty string) to scope the cookie to the exact host only.
    cookieDomain: 'auto',

    // All of your own web properties. Links pointing at these hosts ALWAYS
    // get UTMs decorated, even across different root domains, so attribution
    // carries when a user hops from adrenaline.com -> shop.adrenaline.com -> teamgear.com
    // Accepts bare hosts ('adrenaline.com') — subdomains match automatically.
    // Single 'adrln.com' entry matches the root AND every subdomain
    // (www.adrln.com, shop.adrln.com, teamcenter.adrln.com, and anything new).
    // Add additional root domains here if other TLDs get added later.
    ownDomains: [
      'adrln.com'
    ],

    // --- Platform ---
    // 'auto' | 'shopify' | 'wordpress' | 'salesforce' | 'generic'
    platform: 'auto',

    // --- URL sync (keep UTMs visible in the address bar) ---
    // 'replace' : history.replaceState, no reload (RECOMMENDED)
    // 'reload'  : hard page reload with params appended
    // 'none'    : leave URL alone
    syncUrlMode: 'replace',
    // Don't touch the URL on these paths — checkout flows can break.
    syncUrlSkipPaths: ['/checkout', '/checkouts', '/cart', '/account/login', '/wp-admin', '/wp-login'],
    syncUrlReloadFlag: '_attr_synced',

    // --- Storage keys / retention ---
    lastTouchCookieName: '_attr_last',
    firstTouchCookieName: '_attr_first',
    sessionStorageKey: '_attr_session',
    lastTouchDays: 30,
    firstTouchDays: 180,

    // --- Params to capture ---
    trackedParams: [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'utm_id', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic',
      'gclid', 'gbraid', 'wbraid', 'gad_source',
      'fbclid',
      'msclkid',
      'ttclid',
      'twclid',
      'li_fat_id',
      'yclid',
      'dclid',
      'epik',
      'irclickid',
      // Klaviyo
      '_kx',   // Klaviyo profile identifier from email links
      '_kl',   // Klaviyo list context (used by some flows)
      // Generic referral keys
      'ref', 'referrer', 'source', 'aff', 'affiliate'
    ],

    // --- Klaviyo integration ---
    // When true, pushes attribution data into the Klaviyo profile so UTMs
    // attach to the lead in Klaviyo (and flow through to Salesforce via
    // Klaviyo's native sync, if configured).
    pushToKlaviyo: true,

    // --- Link propagation ---
    propagateToInternal: true,
    propagateToOwnDomains: true, // propagate to domains in ownDomains[]
    propagateToExternal: true,   // propagate to other third-party external links
    externalAllowlist: [],       // if set, external propagation limited to these
    externalBlocklist: [
      'facebook.com', 'fb.com', 'm.facebook.com',
      'twitter.com', 'x.com', 't.co',
      'linkedin.com', 'lnkd.in',
      'pinterest.com', 'pin.it',
      'instagram.com',
      'tiktok.com',
      'youtube.com', 'youtu.be',
      'whatsapp.com', 'wa.me',
      'paypal.com',
      'apple.com', 'itunes.apple.com',
      'google.com/maps'
    ],
    overwriteExistingLinkParams: false,

    // --- Forms ---
    autoFillFormFields: true,
    autoFillFirstTouchFields: true,

    // --- Platform hooks ---
    pushToShopifyCart: true,
    cartAttributePrefix: ''
  };

  // ============ DOMAIN / PLATFORM DETECTION ============
  function deriveCookieDomain() {
    if (CONFIG.cookieDomain && CONFIG.cookieDomain !== 'auto') return CONFIG.cookieDomain;
    var host = location.hostname;
    if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return '';
    var parts = host.split('.');
    if (parts.length <= 2) return host; // example.com or localhost-like
    // Two-level TLDs we care about
    var multi2 = [
      'co.uk','org.uk','ac.uk','gov.uk','me.uk',
      'co.jp','or.jp','ne.jp','ac.jp',
      'com.au','net.au','org.au','edu.au',
      'co.nz','net.nz','org.nz',
      'com.br','com.mx','com.ar','com.tr',
      'co.in','co.za','co.kr','co.il','co.id'
    ];
    var last2 = parts.slice(-2).join('.');
    if (multi2.indexOf(last2) !== -1) return parts.slice(-3).join('.');
    return last2;
  }

  var COOKIE_DOMAIN = deriveCookieDomain();

  function detectPlatform() {
    if (CONFIG.platform && CONFIG.platform !== 'auto') return CONFIG.platform;
    if (window.Shopify || document.querySelector('meta[name="shopify-checkout-api-token"]') ||
        document.querySelector('link[href*="cdn.shopify.com"]')) return 'shopify';
    if (window.wp || document.querySelector('meta[name="generator"][content*="WordPress" i]') ||
        /\bwp-/.test(document.body && document.body.className || '') ||
        document.querySelector('link[href*="/wp-content/"], script[src*="/wp-content/"]')) return 'wordpress';
    if (window.dw || window.SitePreferences || window.CQuotient ||
        document.querySelector('[data-sfcc], [data-demandware]') ||
        /demandware|commercecloud/i.test(document.documentElement.outerHTML.slice(0, 2000))) return 'salesforce';
    return 'generic';
  }

  var PLATFORM = detectPlatform();

  // ============ COOKIE HELPERS ============
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    var domainPart = COOKIE_DOMAIN ? '; domain=' + COOKIE_DOMAIN : '';
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      encodeURIComponent(name) + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() +
      '; path=/' + domainPart +
      '; SameSite=Lax' + secure;
  }

  function getCookie(name) {
    var esc = name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1');
    var m = document.cookie.match(new RegExp('(?:^|; )' + esc + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function deleteCookie(name) {
    var domainPart = COOKIE_DOMAIN ? '; domain=' + COOKIE_DOMAIN : '';
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + domainPart;
  }

  function readJSON(name) {
    try { var v = getCookie(name); return v ? JSON.parse(v) : null; }
    catch (e) { return null; }
  }
  function writeJSON(name, obj, days) { setCookie(name, JSON.stringify(obj), days); }

  // ============ PARAM EXTRACTION ============
  function getCurrentURLParams() {
    var out = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      CONFIG.trackedParams.forEach(function (k) {
        var v = sp.get(k);
        if (v) out[k] = v;
      });
    } catch (e) { /* noop */ }
    return out;
  }
  function hasKeys(o) {
    if (!o) return false;
    for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) return true;
    return false;
  }

  // ============ CAPTURE ============
  function captureAttribution() {
    var current = getCurrentURLParams();
    if (!hasKeys(current)) return;
    var payload = Object.assign({}, current, {
      _ts: new Date().toISOString(),
      _ref: document.referrer || '',
      _lp: window.location.pathname + window.location.search,
      _host: window.location.hostname,
      _platform: PLATFORM
    });
    if (!readJSON(CONFIG.firstTouchCookieName)) {
      writeJSON(CONFIG.firstTouchCookieName, payload, CONFIG.firstTouchDays);
    }
    writeJSON(CONFIG.lastTouchCookieName, payload, CONFIG.lastTouchDays);
    try { sessionStorage.setItem(CONFIG.sessionStorageKey, JSON.stringify(payload)); }
    catch (e) {}
  }

  function getEffectiveParams() {
    var current = getCurrentURLParams();
    if (hasKeys(current)) return current;
    var stored = readJSON(CONFIG.lastTouchCookieName) || {};
    var clean = {};
    for (var k in stored) if (stored.hasOwnProperty(k) && k.charAt(0) !== '_') clean[k] = stored[k];
    return clean;
  }
  function getFirstTouchParams() {
    var stored = readJSON(CONFIG.firstTouchCookieName) || {};
    var clean = {};
    for (var k in stored) if (stored.hasOwnProperty(k) && k.charAt(0) !== '_') clean[k] = stored[k];
    return clean;
  }

  // ============ URL SYNC (keep UTMs in address bar) ============
  function shouldSkipSyncForPath() {
    var path = (window.location.pathname || '').toLowerCase();
    return CONFIG.syncUrlSkipPaths.some(function (p) { return path.indexOf(p.toLowerCase()) === 0; });
  }

  function syncUrlWithStoredParams() {
    if (CONFIG.syncUrlMode === 'none') return;
    if (shouldSkipSyncForPath()) return;

    var stored = readJSON(CONFIG.lastTouchCookieName) || {};
    var cleanStored = {};
    for (var k in stored) if (stored.hasOwnProperty(k) && k.charAt(0) !== '_') cleanStored[k] = stored[k];
    if (!hasKeys(cleanStored)) return;

    var urlParams;
    try { urlParams = new URLSearchParams(window.location.search); }
    catch (e) { return; }

    var changed = false;
    Object.keys(cleanStored).forEach(function (key) {
      if (cleanStored[key] && !urlParams.has(key)) {
        urlParams.set(key, cleanStored[key]);
        changed = true;
      }
    });
    if (!changed) return;

    var qs = urlParams.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;

    if (CONFIG.syncUrlMode === 'reload') {
      try {
        if (sessionStorage.getItem(CONFIG.syncUrlReloadFlag) === window.location.pathname) {
          sessionStorage.removeItem(CONFIG.syncUrlReloadFlag);
          return;
        }
        sessionStorage.setItem(CONFIG.syncUrlReloadFlag, window.location.pathname);
      } catch (e) {}
      window.location.replace(newUrl);
      return;
    }

    if (window.history && typeof window.history.replaceState === 'function') {
      try { window.history.replaceState(window.history.state, '', newUrl); } catch (e) {}
    }
  }

  // ============ LINK UTILITIES ============
  function toURL(href) { try { return new URL(href, window.location.href); } catch (e) { return null; } }
  function isInternal(u) { return u && u.hostname === window.location.hostname; }

  function hostMatches(host, pattern) {
    host = (host || '').toLowerCase();
    pattern = (pattern || '').toLowerCase();
    var slash = pattern.indexOf('/');
    if (slash !== -1) {
      var ph = pattern.slice(0, slash);
      return host === ph || host.endsWith('.' + ph);
    }
    return host === pattern || host.endsWith('.' + pattern);
  }

  function isOwnDomain(u) {
    if (!u) return false;
    return CONFIG.ownDomains.some(function (d) { return hostMatches(u.hostname, d); });
  }
  function isBlocklisted(u) {
    if (!u) return true;
    return CONFIG.externalBlocklist.some(function (p) { return hostMatches(u.hostname, p); });
  }
  function isAllowedExternal(u) {
    if (!CONFIG.propagateToExternal) return false;
    if (isBlocklisted(u)) return false;
    if (CONFIG.externalAllowlist.length === 0) return true;
    return CONFIG.externalAllowlist.some(function (p) { return hostMatches(u.hostname, p); });
  }

  function shouldRewriteLink(href) {
    if (!href) return false;
    if (/^\s*(#|mailto:|tel:|sms:|javascript:|data:)/i.test(href)) return false;
    var u = toURL(href);
    if (!u) return false;
    if (!/^https?:$/.test(u.protocol)) return false;
    if (isInternal(u)) return CONFIG.propagateToInternal;
    if (isOwnDomain(u)) return CONFIG.propagateToOwnDomains;
    return isAllowedExternal(u);
  }

  function appendParamsToHref(href, params) {
    var u = toURL(href);
    if (!u) return href;
    Object.keys(params).forEach(function (k) {
      if (!params[k]) return;
      if (CONFIG.overwriteExistingLinkParams || !u.searchParams.has(k)) {
        u.searchParams.set(k, params[k]);
      }
    });
    return u.toString();
  }

  // ============ APPLY TO LINKS ============
  var processedLinks = typeof WeakSet === 'function' ? new WeakSet() : null;

  function processLink(a, params) {
    if (!a || a.tagName !== 'A') return;
    if (processedLinks && processedLinks.has(a)) return;
    var href = a.getAttribute('href');
    if (!shouldRewriteLink(href)) return;
    var newHref = appendParamsToHref(href, params);
    if (newHref && newHref !== href) a.setAttribute('href', newHref);
    if (processedLinks) processedLinks.add(a);
  }

  function processAllLinks(params) {
    if (!hasKeys(params)) return;
    var anchors = document.getElementsByTagName('a');
    for (var i = 0; i < anchors.length; i++) processLink(anchors[i], params);
  }

  function attachClickInterceptor(params) {
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== document && t.tagName !== 'A') t = t.parentNode;
      if (t && t.tagName === 'A') processLink(t, params);
    }, true);
  }

  function observeMutations(params) {
    if (!window.MutationObserver || !document.body) return;
    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        if (!added) continue;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (!n || n.nodeType !== 1) continue;
          if (n.tagName === 'A') processLink(n, params);
          else if (n.querySelectorAll) {
            var nested = n.querySelectorAll('a');
            for (var k = 0; k < nested.length; k++) processLink(nested[k], params);
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ============ FORM HIDDEN FIELDS ============
  // Covers: plain, Shopify contact[]/customer[]/attributes[]/properties[],
  // WPForms wpforms[fields][], Gravity Forms input_N (by data attr), CF7 (plain),
  // and generic data-attribution="key".
  function formSelectorsFor(key) {
    return [
      'input[name="' + key + '"]',
      'textarea[name="' + key + '"]',
      'input[name="contact[' + key + ']"]',
      'input[name="customer[' + key + ']"]',
      'input[name="attributes[' + key + ']"]',
      'input[name="properties[' + key + ']"]',
      'input[name$="[' + key + ']"]',   // wpforms, GF, nested
      'input[data-attribution="' + key + '"]',
      'input[data-utm="' + key + '"]'
    ].join(',');
  }

  function setFormField(form, key, value) {
    if (!value) return;
    var input = form.querySelector(formSelectorsFor(key));
    if (input) input.value = value;
  }

  function fillHiddenFields(params, firstTouchParams) {
    if (!CONFIG.autoFillFormFields) return;
    var forms = document.querySelectorAll('form');
    forms.forEach(function (form) {
      Object.keys(params).forEach(function (k) { setFormField(form, k, params[k]); });
      if (CONFIG.autoFillFirstTouchFields && firstTouchParams) {
        Object.keys(firstTouchParams).forEach(function (k) {
          setFormField(form, k + '_first', firstTouchParams[k]);
          setFormField(form, k + '_first_touch', firstTouchParams[k]);
        });
      }
    });
  }

  // ============ PLATFORM HOOKS ============
  function pushToShopifyCart(params, firstTouchParams) {
    if (PLATFORM !== 'shopify') return;
    if (!CONFIG.pushToShopifyCart || !hasKeys(params)) return;
    if (!window.fetch) return;
    var attrs = {};
    var last = readJSON(CONFIG.lastTouchCookieName) || {};
    Object.keys(params).forEach(function (k) { attrs[CONFIG.cartAttributePrefix + k] = params[k]; });
    if (firstTouchParams) {
      Object.keys(firstTouchParams).forEach(function (k) {
        attrs[CONFIG.cartAttributePrefix + k + '_first'] = firstTouchParams[k];
      });
    }
    if (last._ts) attrs[CONFIG.cartAttributePrefix + 'attribution_captured_at'] = last._ts;
    if (last._ref) attrs[CONFIG.cartAttributePrefix + 'attribution_referrer'] = last._ref;
    if (last._lp)  attrs[CONFIG.cartAttributePrefix + 'attribution_landing_page'] = last._lp;
    if (last._host) attrs[CONFIG.cartAttributePrefix + 'attribution_capture_host'] = last._host;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
      credentials: 'same-origin'
    }).catch(function () { /* silent */ });
  }

  // ============ KLAVIYO INTEGRATION ============
  // Push attribution into the Klaviyo profile so UTMs follow the lead.
  // Works with both the modern klaviyo.identify() and legacy _learnq.push().
  // Safe to call when the profile is anonymous — Klaviyo merges props into
  // the current session and carries them forward when identified.
  function pushAttributionToKlaviyo() {
    if (!CONFIG.pushToKlaviyo) return;
    var params = getEffectiveParams();
    var firstTouch = getFirstTouchParams();
    if (!hasKeys(params) && !hasKeys(firstTouch)) return;

    var props = {};
    Object.keys(params).forEach(function (k) { props[k] = params[k]; });
    Object.keys(firstTouch).forEach(function (k) { props[k + '_first_touch'] = firstTouch[k]; });

    // Add capture metadata
    var last = readJSON(CONFIG.lastTouchCookieName) || {};
    if (last._ts) props.attribution_captured_at = last._ts;
    if (last._ref) props.attribution_referrer = last._ref;
    if (last._lp) props.attribution_landing_page = last._lp;
    if (last._host) props.attribution_capture_host = last._host;

    // Modern Klaviyo Onsite JS (klaviyo.js)
    if (window.klaviyo) {
      try {
        if (typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify(props);
        } else if (typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['identify', props]);
        }
      } catch (e) { /* silent */ }
    }

    // Legacy learnq queue
    if (window._learnq && typeof window._learnq.push === 'function') {
      try { window._learnq.push(['identify', props]); } catch (e) {}
    }
  }

  function setupKlaviyoHooks() {
    if (!CONFIG.pushToKlaviyo) return;

    // Push now (covers already-identified profiles, e.g. landing from Klaviyo email with _kx)
    pushAttributionToKlaviyo();

    // Klaviyo's form events — fires for signup popups, embedded forms, etc.
    // Event types include: open, submit, stepSubmit, close, embedOpen
    window.addEventListener('klaviyoForms', function (e) {
      if (!e || !e.detail) return;
      var type = e.detail.type;
      if (type === 'submit' || type === 'stepSubmit') {
        // Re-push so the form submission carries full attribution
        pushAttributionToKlaviyo();
      }
    });

    // Re-push once Klaviyo's JS finishes loading (it may not be ready at init time)
    var pollAttempts = 0;
    var poll = setInterval(function () {
      pollAttempts++;
      if (window.klaviyo || window._learnq) {
        pushAttributionToKlaviyo();
        clearInterval(poll);
      }
      if (pollAttempts > 20) clearInterval(poll); // give up after ~10s
    }, 500);
  }

  // ============ PUBLIC API ============
  window.Attribution = {
    get: function () { return getEffectiveParams(); },
    getLastTouch: function () { return readJSON(CONFIG.lastTouchCookieName) || {}; },
    getFirstTouch: function () { return readJSON(CONFIG.firstTouchCookieName) || {}; },
    getSession: function () {
      try { return JSON.parse(sessionStorage.getItem(CONFIG.sessionStorageKey) || '{}'); }
      catch (e) { return {}; }
    },
    getPlatform: function () { return PLATFORM; },
    getCookieDomain: function () { return COOKIE_DOMAIN; },
    syncUrl: syncUrlWithStoredParams,
    pushToKlaviyo: pushAttributionToKlaviyo,
    refill: function () {
      var p = getEffectiveParams();
      var f = getFirstTouchParams();
      processAllLinks(p);
      fillHiddenFields(p, f);
    },
    clear: function () {
      deleteCookie(CONFIG.lastTouchCookieName);
      deleteCookie(CONFIG.firstTouchCookieName);
      try {
        sessionStorage.removeItem(CONFIG.sessionStorageKey);
        sessionStorage.removeItem(CONFIG.syncUrlReloadFlag);
      } catch (e) {}
    },
    config: CONFIG
  };

  // ============ INIT ============
  // Capture + URL sync run SYNCHRONOUSLY so analytics scripts that read
  // location.search on startup see the decorated URL.
  captureAttribution();
  syncUrlWithStoredParams();

  function initDomFeatures() {
    var params = getEffectiveParams();
    var firstTouch = getFirstTouchParams();
    processAllLinks(params);
    attachClickInterceptor(params);
    observeMutations(params);
    fillHiddenFields(params, firstTouch);
    pushToShopifyCart(params, firstTouch);
    setupKlaviyoHooks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDomFeatures);
  } else {
    initDomFeatures();
  }
})();
